// frontend/src/admin/blockforms/TableForm.js
import React, { useState, useEffect, useRef } from 'react';
import { buildBlockSave } from './blockSaveTarget';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://80.225.197.31:8001';

const emptyTable = () => ({
    title: '',
    columns: [
        { header: 'Column 1', width: '25%' },
        { header: 'Column 2', width: '25%' },
        { header: 'Column 3', width: '25%' },
        { header: 'Column 4', width: '25%' }
    ],
    rows: [['', '', '', '']],
    has_header: true,
    table_style: 'default'
});

const TableEditor = ({ table, setter, label }) => {
    const pasteRef = useRef(null);

    const updateField = (field, value) => setter({ ...table, [field]: value });

    const updateColumnHeader = (colIndex, value) => {
        const columns = table.columns.map((col, i) => i === colIndex ? { ...col, header: value } : col);
        setter({ ...table, columns });
    };

    const updateColumnWidth = (colIndex, value) => {
        const columns = table.columns.map((col, i) => i === colIndex ? { ...col, width: value } : col);
        setter({ ...table, columns });
    };

    const addColumn = () => {
        setter({
            ...table,
            columns: [...table.columns, { header: `Column ${table.columns.length + 1}`, width: 'auto' }],
            rows: table.rows.map(r => [...r, ''])
        });
    };

    const removeColumn = (colIndex) => {
        if (table.columns.length <= 1) return;
        setter({
            ...table,
            columns: table.columns.filter((_, i) => i !== colIndex),
            rows: table.rows.map(r => r.filter((_, i) => i !== colIndex))
        });
    };

    const updateCell = (rowIndex, colIndex, value) => {
        const rows = table.rows.map((row, ri) =>
            ri === rowIndex ? row.map((cell, ci) => ci === colIndex ? value : cell) : row
        );
        setter({ ...table, rows });
    };

    const addRow = () => {
        setter({ ...table, rows: [...table.rows, table.columns.map(() => '')] });
    };

    const removeRow = (rowIndex) => {
        if (table.rows.length <= 1) return;
        setter({ ...table, rows: table.rows.filter((_, i) => i !== rowIndex) });
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');
        const pastedRows = text.trim().split('\n').map(row => row.split('\t'));
        if (pastedRows.length === 0) return;

        // Column count = widest pasted row
        const colCount = pastedRows.reduce((m, r) => Math.max(m, r.length), 0);
        const pct = `${Math.floor(100 / colCount)}%`;

        let newColumns;
        let dataRows;

        if (table.has_header) {
            // First row defines the headers; the rest is data
            newColumns = pastedRows[0].map((header) => ({ header: header.trim(), width: pct }));
            dataRows = pastedRows.slice(1);
        } else {
            // No header: every row is data, columns get blank headers
            newColumns = Array.from({ length: colCount }, () => ({ header: '', width: pct }));
            dataRows = pastedRows;
        }

        const newRows = dataRows.map(row => {
            const padded = [...row];
            while (padded.length < newColumns.length) padded.push('');
            return padded.slice(0, newColumns.length).map(c => c.trim());
        });

        setter({
            ...table,
            columns: newColumns,
            rows: newRows.length > 0 ? newRows : [Array(newColumns.length).fill('')]
        });
    };

    return (
        <div className="ace-table-editor">
            <label>{label} — Title</label>
            <input type="text" value={table.title} onChange={(e) => updateField('title', e.target.value)} />

            <div className="ace-table-options">
                <label className="ace-inline-label">
                    <input
                        type="checkbox"
                        checked={table.has_header}
                        onChange={(e) => updateField('has_header', e.target.checked)}
                    />
                    &nbsp;First row is header
                </label>
                <label className="ace-inline-label">
                    Style:&nbsp;
                    <select value={table.table_style} onChange={(e) => updateField('table_style', e.target.value)}>
                        <option value="default">Default</option>
                        <option value="ledger">Ledger (Accounts)</option>
                        <option value="comparison">Comparison</option>
                        <option value="highlight">Highlight</option>
                    </select>
                </label>
            </div>

            <div
                className="ace-paste-area"
                onPaste={handlePaste}
                tabIndex={0}
                ref={pasteRef}
            >
                📋 Click here and paste Excel data (Ctrl+V) — or build manually below
            </div>

            <label>Columns</label>
            <div className="ace-table-columns-row">
                {table.columns.map((col, ci) => (
                    <div key={ci} className="ace-table-col-header">
                        <input
                            type="text"
                            value={col.header}
                            placeholder={`Column ${ci + 1}`}
                            onChange={(e) => updateColumnHeader(ci, e.target.value)}
                        />
                        <input
                            type="text"
                            className="ace-col-width"
                            value={col.width}
                            placeholder="25%"
                            title="Column width (e.g. 25%)"
                            onChange={(e) => updateColumnWidth(ci, e.target.value)}
                        />
                        <button type="button" onClick={() => removeColumn(ci)}>✕</button>
                    </div>
                ))}
                <button type="button" onClick={addColumn}>+ Column</button>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label>Rows</label>
                <button
                    type="button"
                    onClick={() => setter(emptyTable())}
                    style={{ fontSize: '0.85em', color: '#b71c1c' }}
                >
                    🗑 Clear Table
                </button>
            </div>
            <div className="ace-table-grid-wrapper">
                <table className="ace-table-grid">
                    <tbody>
                        {table.rows.map((row, ri) => (
                            <tr key={ri}>
                                <td className="ace-row-num">{ri + 1}</td>
                                {row.map((cell, ci) => (
                                    <td key={ci}>
                                        <input
                                            type="text"
                                            value={cell}
                                            onChange={(e) => updateCell(ri, ci, e.target.value)}
                                        />
                                    </td>
                                ))}
                                <td>
                                    <button type="button" onClick={() => removeRow(ri)}>✕</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <button type="button" onClick={addRow}>+ Add Row</button>
        </div>
    );
};

const TableForm = ({ level, entityId, section, editingBlock, nextOrderIndex, onSaved, onCancel }) => {
    const existing = editingBlock?.content_data || {};

    const [tableEn, setTableEn] = useState(existing.en || emptyTable());
    const [tableHi, setTableHi] = useState(existing.hi || null);
    const [addHindi, setAddHindi] = useState(!!existing.hi);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [tableEn, tableHi]);

    const handleSave = async () => {
        if (tableEn.has_header && !tableEn.columns.some(c => c.header.trim())) {
            setError('Please add at least one column header (English)');
            return;
        }
        setError(null);
        setSaving(true);

        const content_data = { en: tableEn };
        if (addHindi && tableHi) {
            content_data.hi = tableHi;
        }

        const { url, method, payload } = buildBlockSave({
            level, entityId, section, editingBlock,
            blockType: 'table',
            contentData: content_data,
            orderIndex: nextOrderIndex,
        });

        try {
            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.detail || 'Save failed');
            }
            onSaved();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="ace-form">
            <h4>{editingBlock ? 'Edit' : 'Add'} Table</h4>
            {error && <div className="ace-error">{error}</div>}

            <TableEditor table={tableEn} setter={setTableEn} label="English Table" />

            {!addHindi && (
                <button type="button" onClick={() => { setAddHindi(true); setTableHi(emptyTable()); }}>
                    + Add Hindi Version
                </button>
            )}

            {addHindi && tableHi && <TableEditor table={tableHi} setter={setTableHi} label="Hindi Table" />}

            <div className="ace-form-actions">
                <button onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                <button onClick={onCancel} className="ace-cancel-btn">Cancel</button>
            </div>
        </div>
    );
};

export default TableForm;